import asn1 from './asn1'
import OID from './OID'
import * as utils from './utils'
import { Buffer } from 'buffer'

function BytesToHexadecimal (bytes) {
  const hex = []
  bytes.slice(1).forEach((b) => hex.push(('0' + b.toString(16)).slice(-2)))
  return hex.join(':')
}

const TbsTokenOrder = Object.freeze({
  Version: 0,
  SerialNumber: 1,
  SignatureAlgorithm: 2,
  Issuer: 3,
  Validity: 4,
  Subject: 5,
  SubjectPublicKeyInfo: 6,
  OptionalIssuerUniqueId: 7,
  OptionalSubjectUniqueId: 8,
  Extensions: 9
})

function PARSE_TBS_TOKEN (order, token) {
  switch (order) {
    case TbsTokenOrder.Version:
      return ParseTokenVersion(token)
    case TbsTokenOrder.SerialNumber:
      return ParseSerialNumber(token)
    case TbsTokenOrder.SignatureAlgorithm:
      return ParseSignatureAlgorithm(token)
    case TbsTokenOrder.Issuer:
      return ParseIssuer(token)
    case TbsTokenOrder.Validity:
      return ParseValidity(token)
    case TbsTokenOrder.Subject:
      return ParseSubject(token)
    case TbsTokenOrder.SubjectPublicKeyInfo:
      return ParseSubjectPublicKeyInfo(token)
    default:
      return []
  }
}

function ParseTokenVersion (token) {
  const versionTlv = asn1.tokenize(token.value)[0]
  return parseInt(versionTlv.parsedResult) + 1
}

function ParseSerialNumber (token) {
  return {
    Int: token.parsedResult,
    Hex: BytesToHexadecimal(token.value)
  }
}

function ParseSignatureAlgorithm (token) {
  return {
    oid: token.parsedResult[0].parsedResult,
    algo: OID.lookup(token.parsedResult[0].parsedResult)
  }
}

function ParseIssuer (token) {
  const issuerObj = {}
  const parts = token.parsedResult.map((token) => {
    const fields = token.parsedResult[0].parsedResult
    const oid = fields[0].parsedResult
    return {
      oid: oid,
      key: OID.lookup(oid),
      value: fields[1].parsedResult
    }
  })

  const issuer = parts.map((part) => `${part.key}=${part.value}`).join(', ')

  parts.forEach((part) => {
    issuerObj[part.key] = part.value
  })
  issuerObj.full = issuer

  return issuerObj
}

function ParseValidity (token) {
  return {
    NotBefore: token.parsedResult[1].parsedResult,
    NotAfter: token.parsedResult[0].parsedResult
  }
}

function ParseSubject (token) {
  const subjectObj = {}

  const parts = token.parsedResult.map((token) => {
    const fields = token.parsedResult[0].parsedResult
    const oid = fields[0].parsedResult
    const Key = OID.lookup(oid)
    const Value = fields[1].parsedResult
    return {
      OID: oid,
      Key,
      Value
    }
  })
  const Full = parts.map((p) => `${p.Key}=${p.Value}`).join(', ')
  parts.forEach((part) => {
    subjectObj[part.Key] = part.Value
  })

  return { ...subjectObj, Full }
}

function ParseSubjectPublicKeyInfo (token) {
  const subjectPublicKeyInfoParams = token.parsedResult[0].parsedResult.map((t) => {
    const oid = t.parsedResult
    const Param = OID.lookup(oid)
    return {
      OID: oid,
      Param
    }
  })
  const subjectPublicKeyInfoKey = token.parsedResult[1].parsedResult.hex
  const subjectPublicKeyInfo = {
    Params: subjectPublicKeyInfoParams,
    Key: subjectPublicKeyInfoKey
  }
  return subjectPublicKeyInfo
}

function parseIssuerUniqueId (token) {
  throw new Error('Issuer unique id not supported.')
}

function parseSubjectUniqueId (token) {
  throw new Error('Subject unique id not supported.')
}

function parseExtension_SubjectKeyIdentifier (extensionObj, token) {
  const subjectKeyIdentifierBytes = asn1.tokenize(token.value)[0].parsedResult.hex
  extensionObj.hex = subjectKeyIdentifierBytes
  return extensionObj
}

function parseExtension_AuthorityInformationAccess (extensionObj, token) {
  const tokens = asn1.tokenize(token.value)[0].parsedResult
  tokens.forEach((seq) => {
    const oid = seq.parsedResult[0].parsedResult
    const oidString = OID.lookup(oid)
    const generalName = seq.parsedResult[1].value.toString()
    extensionObj[oidString] = generalName
  })
  return extensionObj
}

function parseExtension_ExtendedKeyUsage (extensionObj, token) {
  const objects = asn1.tokenize(token.value)[0].parsedResult
  const usages = objects.map((obj) => {
    const oid = obj.parsedResult
    const usage = OID.lookup(oid)
    return usage
  })

  extensionObj.usages = usages
  return extensionObj
}

function parseExtensionsFromObject (extensionObj, token) {
  const tokens = asn1.tokenize(token.value)[0]
  extensionObj.hex = tokens.parsedResult.hex
  return extensionObj
}

function parseExtension_AuthorityKeyIdentifier (extensionObj, token) {
  const tokens = asn1.tokenize(token.value)[0].parsedResult
  tokens.forEach((token) => {
    const contextualTag = token.tagStr
    if (contextualTag === 'cont [ 0 ]') {
      extensionObj.KeyIdentifier = utils.bytesToHex(token.value)
    } else if (contextualTag === 'cont [ 1 ]') {
      extensionObj.GeneralNames = utils.bytesToHex(token.value)
    } else if (contextualTag === 'cont [ 2 ]') {
      extensionObj.CertificateSerialNumber = utils.bytesToHex(token.value)
    }
  })
  return extensionObj
}

function parseExtension_SubjectAlternativeName (extensionObj, token) {
  const altNameTokens = asn1.tokenize(token.value)[0].parsedResult
  const alternativeNames = altNameTokens.map((nameToken) => {
    switch (nameToken.tagStr) {
      case 'cont [ 1 ]':
      case 'cont [ 2 ]':
        return nameToken.value.toString()
      default:
    }
  })
  extensionObj.alternativeNames = alternativeNames
  return extensionObj
}

function parseExtension_KeyUsage (extensionObj, token) {
  const bytes = token.value
  const usages = []

  if (bytes.readUInt8(1) === 0x02) {
    const bitshift = bytes.readUInt8(2)
    const flags = bytes.readUInt8(3) >>> bitshift
    if ((flags & 0x01) === 0x01) usages.push('Digital Signature')
    if ((flags & 0x02) === 0x02) usages.push('Content Commitment (Certificate Signing)')
    if ((flags & 0x04) === 0x04) usages.push('Key Encipherment')
    if ((flags & 0x08) === 0x08) usages.push('Data Encipherment')
    if ((flags & 0x10) === 0x10) usages.push('Key Agreement')
    if ((flags & 0x20) === 0x20) usages.push('Key Cert Sign')
    if ((flags & 0x40) === 0x40) usages.push('cRLSign')
    if ((flags & 0x80) === 0x80) usages.push('Data Encipherment')
  }
  extensionObj.usages = usages
  return extensionObj
}

function ParseExtensionCertificatePolicies (extensionObj, token) {
  function parseQualifier (qualifierToken) {
    const arr = qualifierToken.parsedResult[0].parsedResult

    return {
      oid: arr[0].parsedResult,
      str: arr[1].parsedResult
    }
  }
  const tokens = asn1.tokenize(token.value)[0].parsedResult

  const policies = tokens.map((t) => {
    const policyOid = t.parsedResult[0].parsedResult
    const optionalQualifierTokens = t.parsedResult.slice(1)
    const optionalQualifiers = optionalQualifierTokens.map((oqToken) => parseQualifier(oqToken))

    return {
      oid: policyOid,
      qualifiers: optionalQualifiers
    }
  })
  extensionObj.policies = policies
  return extensionObj
}

function parseExtension_BasicConstraints (extensionObj, token) {
  extensionObj.cA = false

  const tokens = asn1.tokenize(token.value)[0].parsedResult
  if (tokens[0] !== undefined) {
    extensionObj.cA = tokens[0].parsedResult
  }

  if (tokens[1] !== undefined) {
    const pathLenConstraint = parseInt(tokens[1].parsedResult)
    extensionObj.pathLenConstraint = pathLenConstraint
  }
  return extensionObj
}

function parseExtensions (token) {
  const extensionTokens = asn1.tokenize(token.value)[0].parsedResult

  const extensions = extensionTokens.map((extensionToken) => {
    const tokenCount = extensionToken.parsedResult.length
    const oid = extensionToken.parsedResult[0].parsedResult
    const extensionName = OID.lookup(oid)

    let critical = false
    let octetToken
    if (tokenCount === 2) {
      octetToken = extensionToken.parsedResult[1]
    } else {
      critical = extensionToken.parsedResult[1].parsedResult
      octetToken = extensionToken.parsedResult[2]
    }
    const extensionObj = {
      extnID: extensionName,
      critical: critical
    }
    switch (oid) {
      case '2.5.29.14':
        parseExtension_SubjectKeyIdentifier(extensionObj, octetToken)
        break
      case '2.5.29.15':
        parseExtension_KeyUsage(extensionObj, octetToken)
        break
      case '2.5.29.17':
        parseExtension_SubjectAlternativeName(extensionObj, octetToken)
        break
      case '2.5.29.19':
        parseExtension_BasicConstraints(extensionObj, octetToken)
        break
      case '2.5.29.31':
        break
      case '2.5.29.32':
        ParseExtensionCertificatePolicies(extensionObj, octetToken)
        break
      case '2.5.29.33':
        break
      case '2.5.29.35':
        parseExtension_AuthorityKeyIdentifier(extensionObj, octetToken)
        break
      case '2.5.29.37':
        parseExtension_ExtendedKeyUsage(extensionObj, octetToken)
        break
      case '1.3.6.1.5.5.7.1.1':
        parseExtension_AuthorityInformationAccess(extensionObj, octetToken)
        break
      case '1.3.6.1.4.1.11129.2.4.2':
        parseExtensionsFromObject(extensionObj, octetToken)
        break
      default:
        // nothing
        break
    }
    return extensionObj
  })

  // The comments below are quotes pasted from rfc 5280 section 4.2. These are important

  // When an extension appears in a certificate, the OID appears as the field
  // extnID and the corresponding ASN.1 DER encoded structure is the value
  // of the octet string extnValue.

  // An extension includes the boolean critical, with a default value of FALSE.

  // Conforming CAs MUST support key identifiers (Sections 4.2.1.1 and
  // 4.2.1.2), basic constraints (Section 4.2.1.9), key usage (Section
  // 4.2.1.3), and certificate policies (Section 4.2.1.4) extensions.

  // If the CA issues certificates with an empty sequence for the subject
  // field, the CA MUST support the subject alternative name extension
  // (Section 4.2.1.6).

  // At a minimum, applications conforming to this profile MUST recognize
  //    the following extensions: key usage (Section 4.2.1.3), certificate
  //    policies (Section 4.2.1.4), subject alternative name (Section
  //    4.2.1.6), basic constraints (Section 4.2.1.9), name constraints
  //    (Section 4.2.1.10), policy constraints (Section 4.2.1.11), extended
  //    key usage (Section 4.2.1.12), and inhibit anyPolicy (Section
  //    4.2.1.14).
  //    In addition, applications conforming to this profile SHOULD recognize
  //    the authority and subject key identifier (Sections 4.2.1.1 and
  //    4.2.1.2) and policy mappings (Section 4.2.1.5) extensions.

  // extnID is OID
  // value is OCTET string, which is actually a ASN.1 DER encoded struct
  return extensions
}

function parseSignatureAlgorithmTokens (tokens) {
  const oid = tokens[0].parsedResult
  const algo = OID.lookup(oid)

  tokens.slice(1).map((token) => {
    return token.parsedResult
  })

  const params = []

  return {
    oid: oid,
    algo: algo,
    params: params
  }
}

function parsePem (pem) {
  const b64 = pem.toString().replace(/(-----(BEGIN|END) CERTIFICATE-----|[\n\r])/g, '')
  const buf = Buffer.from(b64, 'base64')
  return parse(buf)
}

function isPem (data) {
  return data.includes('BEGIN CERTIFICATE')
}

function parse (buffer) {
  if (isPem(buffer)) {
    return parsePem(buffer)
  } else {
    return parseDer(buffer)
  }
}

function parseDer (buffer) {
  const tlvs = asn1.tokenize(buffer)
  const certificateTokens = tlvs[0].parsedResult
  const tbsTokens = certificateTokens[0].parsedResult
  const signatureAlgorithmTokens = certificateTokens[1].parsedResult
  const SignatureValue = certificateTokens[2].parsedResult
  const requiredTokens = tbsTokens.slice(0, TbsTokenOrder.SubjectPublicKeyInfo + 1)
  const optionalTokens = tbsTokens.slice(TbsTokenOrder.OptionalIssuerUniqueId)
  const parsedTbsTokens = requiredTokens.map((token, i) => {
    return PARSE_TBS_TOKEN(i, token)
  })
  const tbs = {
    Version: parsedTbsTokens[TbsTokenOrder.Version],
    SerialNumber: parsedTbsTokens[TbsTokenOrder.SerialNumber],
    SignatureAlgorithm: parsedTbsTokens[TbsTokenOrder.SignatureAlgorithm],
    Issuer: parsedTbsTokens[TbsTokenOrder.Issuer],
    Validity: parsedTbsTokens[TbsTokenOrder.Validity],
    Subject: parsedTbsTokens[TbsTokenOrder.Subject],
    SubjectPublicKeyInfo: parsedTbsTokens[TbsTokenOrder.SubjectPublicKeyInfo]
  }
  let issuerUniqueId
  let subjectUniqueId
  let extensions

  optionalTokens.forEach((token) => {
    if (token.tagStr === 'cont [ 1 ]') {
      issuerUniqueId = parseIssuerUniqueId(token)
    } else if (token.tagStr === 'cont [ 2 ]') {
      subjectUniqueId = parseSubjectUniqueId(token)
    } else if (token.tagStr === 'cont [ 3 ]') {
      extensions = parseExtensions(token)
    }
  })
  if (issuerUniqueId !== undefined) {
    tbs.IssuerUniqueId = issuerUniqueId
  }
  if (subjectUniqueId !== undefined) {
    tbs.SubjectUniqueId = subjectUniqueId
  }
  if (extensions !== undefined) {
    tbs.Extensions = extensions
  }

  const SignatureAlgorithm = parseSignatureAlgorithmTokens(signatureAlgorithmTokens)
  return {
    ...tbs,
    SignatureAlgorithm,
    SignatureValue
  }
}

// exports.parseVersion = parseVersion
// exports.parseExtension_AuthorityKeyIdentifier = parseExtension_AuthorityKeyIdentifier
// exports.parseExtension_BasicConstraints = parseExtension_BasicConstraints
// exports.parseExtension_CertificatePolicies = parseExtension_CertificatePolicies
export { parsePem }
// exports.parseDer = parseDer
// exports.parse = parse
// exports.parseFromFile = parseFromFile
